import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import sessionValidationService from '../services/sessionValidationService';

/**
 * Hook for session validation and management
 * @param {Object} options - Configuration options
 * @param {boolean} options.validateOnMount - Whether to validate session when component mounts
 * @param {boolean} options.periodicValidation - Whether to enable periodic validation
 * @param {number} options.validationInterval - Interval for periodic validation (ms)
 * @returns {Object} - Session validation state and methods
 */
export const useSessionValidation = (options = {}) => {
  const {
    validateOnMount = true,
    periodicValidation = false,
    validationInterval = 10 * 60 * 1000 // 10 minutes
  } = options;

  const [isValidating, setIsValidating] = useState(false);
  const [lastValidation, setLastValidation] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Get current auth state
  const user = useSelector(state => state.auth.user);
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);

  /**
   * Validate the current session
   */
  const validateSession = useCallback(async (force = false) => {
    if (!user?.access_token) {
      console.log('🔒 No access token, skipping validation');
      return false;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const isValid = force 
        ? await sessionValidationService.forceValidateSession()
        : await sessionValidationService.validateSession();
      
      setLastValidation(new Date());
      return isValid;
    } catch (error) {
      console.error('❌ Session validation error:', error);
      setValidationError(error);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [user?.access_token]);

  /**
   * Check if session appears valid (synchronous)
   */
  const isSessionValid = useCallback(() => {
    return sessionValidationService.isSessionValid();
  }, []);

  /**
   * Force session validation (ignores cache)
   */
  const forceValidateSession = useCallback(async () => {
    return await validateSession(true);
  }, [validateSession]);

  // Validate on mount if enabled
  useEffect(() => {
    if (validateOnMount && user?.access_token) {
      validateSession();
    }
  }, [validateOnMount, user?.access_token, validateSession]);

  // Setup periodic validation if enabled
  useEffect(() => {
    if (!periodicValidation || !user?.access_token) {
      return;
    }

    const interval = setInterval(async () => {
      if (isSessionValid()) {
        await validateSession();
      }
    }, validationInterval);

    return () => clearInterval(interval);
  }, [periodicValidation, user?.access_token, validationInterval, validateSession, isSessionValid]);

  // Auto-validate when user changes
  useEffect(() => {
    if (user?.access_token && !isValidating) {
      validateSession();
    }
  }, [user?.access_token, validateSession, isValidating]);

  return {
    // State
    isValidating,
    lastValidation,
    validationError,
    isLoggedIn,
    
    // Methods
    validateSession,
    forceValidateSession,
    isSessionValid,
    
    // Computed
    hasValidSession: isSessionValid()
  };
};

export default useSessionValidation;
